import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";

const DashboardMarketContext = createContext({
  selectedMarket: "futures",
  setSelectedMarket: () => {},
  permissionType: "both",
  canSwitchMarket: true,
});

export const DASHBOARD_MARKET_OPTIONS = [
  { key: "futures", label: "Futures" },
  { key: "spot", label: "Spot" },
];

export const parsePermissionType = (value) => {
  if (value == null || value === "") return null;
  const normalized = String(value).toLowerCase().trim();
  if (normalized === "none") return null;
  if (normalized === "future") return "futures";
  if (
    normalized === "spot" ||
    normalized === "futures" ||
    normalized === "both"
  ) {
    return normalized;
  }
  return null;
};

/** Resolve connected exchange permissionType for CM/CL dashboards. */
export const getUserTradePermissionType = (user) => {
  const binance = user?.binanceCredentials;
  const bybit = user?.bybitCredentials;
  const raw = binance?.isConnected
    ? binance?.permissionType
    : bybit?.isConnected
      ? bybit?.permissionType
      : binance?.permissionType ?? bybit?.permissionType;
  return parsePermissionType(raw) || "both";
};

/**
 * Resolve a community/CL market recommendation from a community payload.
 * Mirrors marketplace market resolution so Recommended matches the table.
 */
export const resolveCommunityRecommendedPermissionType = (source) => {
  if (!source) return null;

  if (Object.prototype.hasOwnProperty.call(source, "permissionType")) {
    const fromCommunity = parsePermissionType(source.permissionType);
    if (fromCommunity) return fromCommunity;
  }

  const exchange = String(source?.exchange ?? "").toLowerCase().trim();
  if (exchange === "binance") {
    const fromBinance = parsePermissionType(source?.binancePermissionType);
    if (fromBinance) return fromBinance;
  }
  if (exchange === "bybit") {
    const fromBybit = parsePermissionType(source?.bybitPermissionType);
    if (fromBybit) return fromBybit;
  }

  const fromBinance = parsePermissionType(source?.binancePermissionType);
  if (fromBinance) return fromBinance;
  const fromBybit = parsePermissionType(source?.bybitPermissionType);
  if (fromBybit) return fromBybit;

  const leader = source?.leaderUser || source?.leader || source?.owner || null;
  const binance =
    leader?.binanceCredentials || source?.binanceCredentials || null;
  const bybit = leader?.bybitCredentials || source?.bybitCredentials || null;

  const legacyCandidates = [
    source?.tradeMarketType,
    source?.tradeMarket,
    leader?.permissionType,
    binance?.isConnected ? binance?.permissionType : null,
    bybit?.isConnected ? bybit?.permissionType : null,
  ];

  for (const candidate of legacyCandidates) {
    const parsed = parsePermissionType(candidate);
    if (parsed) return parsed;
  }

  return null;
};

export const cacheRecommendedPermissionType = (communityId, permissionType) => {
  const parsed = parsePermissionType(permissionType);
  if (!parsed) return;
  try {
    sessionStorage.setItem("recommendedPermissionType", parsed);
    if (communityId) {
      sessionStorage.setItem(
        "recommendedPermissionCommunityId",
        String(communityId)
      );
    }
  } catch {
    // ignore storage failures
  }
};

/** Community market the CM should match (fresh session cache, then community). */
export const getCommunityRecommendedPermissionType = (user) => {
  const community = user?.memberCommunities?.[0]?.community;
  const communityId = community?._id ? String(community._id) : null;

  try {
    const storedId = sessionStorage.getItem("recommendedPermissionCommunityId");
    const storedType = parsePermissionType(
      sessionStorage.getItem("recommendedPermissionType")
    );
    if (communityId && storedId === communityId && storedType) {
      return storedType;
    }
  } catch {
    // ignore storage failures
  }

  const fromCommunity = resolveCommunityRecommendedPermissionType(community);
  if (fromCommunity) return fromCommunity;

  try {
    return parsePermissionType(
      sessionStorage.getItem("recommendedPermissionType")
    );
  } catch {
    return null;
  }
};

/**
 * Show a sidebar nudge on Copy Trade Settings when the CM's connected
 * market doesn't match the CL community recommendation (e.g. CL=both, CM=futures).
 */
export const shouldShowCopyTradeMarketIndicator = (user) => {
  if (user?.role !== "member" && user?.role !== "memberNoLink") return false;

  const isConnected =
    Boolean(user?.binanceCredentials?.isConnected) ||
    Boolean(user?.bybitCredentials?.isConnected);
  if (!isConnected) return false;

  const recommended = getCommunityRecommendedPermissionType(user);
  if (!recommended) return false;

  const memberType = getUserTradePermissionType(user);
  return memberType !== recommended;
};

export const DashboardMarketProvider = ({ children, forceEnable = false }) => {
  const user = useSelector((state) => state.user.user);
  const permissionType = useMemo(
    () => getUserTradePermissionType(user),
    [
      user?.binanceCredentials?.isConnected,
      user?.binanceCredentials?.permissionType,
      user?.bybitCredentials?.isConnected,
      user?.bybitCredentials?.permissionType,
    ]
  );
  const canSwitchMarket = forceEnable || permissionType === "both";

  const [selectedMarket, setSelectedMarketState] = useState(() =>
    permissionType === "spot" ? "spot" : "futures"
  );

  useEffect(() => {
    if (forceEnable) return;
    if (permissionType === "spot") {
      setSelectedMarketState("spot");
    } else if (permissionType === "futures") {
      setSelectedMarketState("futures");
    }
  }, [permissionType, forceEnable]);

  const setSelectedMarket = useCallback(
    (market) => {
      if (!canSwitchMarket) return;
      const next = market === "spot" ? "spot" : "futures";
      setSelectedMarketState(next);
    },
    [canSwitchMarket]
  );

  const value = useMemo(
    () => ({
      selectedMarket,
      setSelectedMarket,
      permissionType,
      canSwitchMarket,
    }),
    [selectedMarket, setSelectedMarket, permissionType, canSwitchMarket]
  );

  return (
    <DashboardMarketContext.Provider value={value}>
      {children}
    </DashboardMarketContext.Provider>
  );
};

export const useDashboardMarket = () => useContext(DashboardMarketContext);

export default DashboardMarketContext;
